import { ChangeDetectionStrategy, Component, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SPRINT_1_CONTENT } from '../data/course-data';

@Component({
  selector: 'app-sprint1-section',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-12">
      <!-- Section Header -->
      <div class="bg-[#121214] rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl relative overflow-hidden">
        <div class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-semibold mb-3 border border-[#F27D26]/30">
          <span class="material-symbols-outlined text-sm">memory</span>
          <span>SPRINT 1 (10h) • O 'HARDWARE'</span>
        </div>
        <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight mb-2">
          {{ content.title }}
        </h2>
        <p class="text-zinc-400 text-sm max-w-3xl">
          {{ content.subtitle }} — Mapeamento do perfil comportamental e transição da mente de executor técnico para fundador e solucionador de problemas de mercado.
        </p>

        <!-- Core Elements Cards -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8 pt-6 border-t border-white/10">
          @for (item of content.coreElements; track item.title; let i = $index) {
            <div class="p-5 rounded-xl bg-white/[0.02] border border-white/10 hover:border-[#F27D26]/50 transition-all">
              <div class="w-8 h-8 rounded-lg bg-[#F27D26] text-black font-bold text-xs flex items-center justify-center mb-3">
                0{{ i + 1 }}
              </div>
              <h3 class="font-bold text-white text-base mb-2">{{ item.title }}</h3>
              <p class="text-xs text-zinc-400 leading-relaxed">{{ item.text }}</p>
            </div>
          }
        </div>
      </div>

      <!-- Radar Chart & Core Simulator -->
      <div class="bg-blueprint-grid rounded-2xl border border-white/10 p-6 sm:p-8 text-white shadow-xl">
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 pb-4 border-b border-white/10">
          <div>
            <span class="text-xs font-mono font-medium text-[#F27D26] uppercase tracking-wider block mb-1">
              • DIAGNÓSTICO DE COMPETÊNCIAS
            </span>
            <h3 class="text-xl sm:text-2xl font-bold text-white flex items-center space-x-2">
              <span class="material-symbols-outlined text-[#F27D26]">radar</span>
              <span>O Core do Empreendedor em Redes</span>
            </h3>
            <p class="text-xs text-zinc-400 mt-1">Ajuste os 5 eixos para mapear o perfil da sua liderança empreendedora em TIC.</p>
          </div>

          <div class="p-3 rounded-xl bg-[#0A0A0B] border border-white/10 text-right shrink-0">
            <span class="text-[10px] font-mono text-zinc-400 block uppercase">Índice de Prontidão</span>
            <span class="text-xl font-mono font-extrabold text-[#F27D26]">{{ averageScore() }}/100</span>
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <!-- Interactive Sliders -->
          <div class="lg:col-span-6 space-y-4">
            @for (axis of content.radarAxes; track axis.key) {
              <div class="p-3 rounded-xl bg-[#0A0A0B] border border-white/10 space-y-1.5">
                <div class="flex items-center justify-between text-xs">
                  <span class="font-bold text-zinc-200">{{ axis.label }}</span>
                  <span class="font-mono text-[#F27D26] font-bold">{{ scores()[axis.key] }}%</span>
                </div>
                <input 
                  type="range" 
                  min="10" 
                  max="100" 
                  step="5"
                  [value]="scores()[axis.key]"
                  (input)="updateScore(axis.key, $event)"
                  class="w-full h-1.5 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-[#F27D26]" />
                <p class="text-[11px] text-zinc-400">{{ axis.description }}</p>
              </div>
            }
          </div>

          <!-- Radar Chart SVG Visualization -->
          <div class="lg:col-span-6 flex flex-col items-center justify-center p-6 bg-[#0A0A0B] rounded-xl border border-white/10 relative">
            <div class="w-full max-w-md aspect-square relative flex items-center justify-center">
              <svg viewBox="0 0 300 300" class="w-full h-full overflow-visible">
                <!-- Outer Pentagon Grid Lines -->
                <polygon points="150,30 264,113 220,247 80,247 36,113" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="1" stroke-dasharray="3 3"/>
                <polygon points="150,60 231,119 200,218 100,218 69,119" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="1" stroke-dasharray="3 3"/>
                <polygon points="150,90 198,125 180,188 120,188 102,125" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="1" stroke-dasharray="3 3"/>

                <!-- Axis Lines -->
                <line x1="150" y1="150" x2="150" y2="30" stroke="rgba(255,255,255,0.2)" stroke-width="1.5"/>
                <line x1="150" y1="150" x2="264" y2="113" stroke="rgba(255,255,255,0.2)" stroke-width="1.5"/>
                <line x1="150" y1="150" x2="220" y2="247" stroke="rgba(255,255,255,0.2)" stroke-width="1.5"/>
                <line x1="150" y1="150" x2="80" y2="247" stroke="rgba(255,255,255,0.2)" stroke-width="1.5"/>
                <line x1="150" y1="150" x2="36" y2="113" stroke="rgba(255,255,255,0.2)" stroke-width="1.5"/>

                <!-- Polygon Area from Signal Points -->
                <polygon [attr.points]="radarPolygonPoints()" fill="rgba(242, 125, 38, 0.25)" stroke="#F27D26" stroke-width="2.5"/>

                <!-- Points dots -->
                @for (pt of radarDots(); track pt.x) {
                  <circle [attr.cx]="pt.x" [attr.cy]="pt.y" r="4" fill="#F27D26" stroke="#0A0A0B" stroke-width="2"/>
                }

                <!-- Labels -->
                <text x="150" y="15" text-anchor="middle" fill="#F27D26" font-size="10" font-family="monospace" font-weight="bold">Visão de Mercado</text>
                <text x="275" y="115" text-anchor="start" fill="#F27D26" font-size="10" font-family="monospace" font-weight="bold">Liderança Técnica</text>
                <text x="230" y="265" text-anchor="middle" fill="#F27D26" font-size="10" font-family="monospace" font-weight="bold">Resiliência</text>
                <text x="70" y="265" text-anchor="middle" fill="#F27D26" font-size="10" font-family="monospace" font-weight="bold">Inovação</text>
                <text x="25" y="115" text-anchor="end" fill="#F27D26" font-size="10" font-family="monospace" font-weight="bold">Execução</text>
              </svg>
            </div>

            <!-- Archetype Result Card -->
            <div class="mt-4 p-4 rounded-xl bg-[#F27D26]/10 border border-[#F27D26]/30 text-center w-full">
              <span class="text-[10px] font-mono text-zinc-400 block uppercase">Arquétipo Dominante Identificado</span>
              <p class="text-sm font-bold text-[#F27D26] mt-1">{{ archetypeResult().title }}</p>
              <p class="text-xs text-zinc-300 mt-1 leading-relaxed">{{ archetypeResult().desc }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Topologias de Empreendedores -->
      <div class="bg-[#121214] rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl space-y-6">
        <div class="border-b border-white/10 pb-4">
          <span class="text-xs font-mono font-semibold text-[#F27D26] uppercase tracking-wider block mb-1">
            ESTRUTURAS E MODELOS DE ATUAÇÃO
          </span>
          <h3 class="text-2xl font-extrabold text-white">
            Topologias de Empreendedores no Mercado
          </h3>
          <p class="text-zinc-400 text-xs mt-1">
            Diferentes formas como o profissional de infraestrutura de redes aplica suas competências para gerar valor.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          @for (top of content.topologies; track top.title) {
            <div class="p-6 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-[#F27D26]/50 hover:shadow-lg transition-all flex flex-col justify-between space-y-4">
              <div>
                <div class="flex items-center justify-between mb-3">
                  <div class="w-10 h-10 rounded-xl bg-[#F27D26]/10 text-[#F27D26] border border-[#F27D26]/30 flex items-center justify-center">
                    <span class="material-symbols-outlined text-2xl">{{ top.icon }}</span>
                  </div>
                  <span class="px-2.5 py-1 rounded-full bg-white/5 text-zinc-300 text-[11px] font-mono font-medium border border-white/10">
                    {{ top.tag }}
                  </span>
                </div>

                <h4 class="text-lg font-bold text-white mb-2">{{ top.title }}</h4>
                <p class="text-xs text-zinc-400 leading-relaxed">{{ top.description }}</p>
              </div>
            </div>
          }
        </div>
      </div>
    </section>
  `
})
export class Sprint1SectionComponent {
  content = SPRINT_1_CONTENT;

  scores = signal<Record<string, number>>({
    visao: 75,
    lideranca: 85,
    resiliencia: 80,
    inovacao: 70,
    execucao: 80
  });

  updateScore(key: string, event: Event) {
    const val = Number((event.target as HTMLInputElement).value);
    this.scores.update(s => ({ ...s, [key]: val }));
  }

  averageScore = computed(() => {
    const s = this.scores();
    const sum = s['visao'] + s['lideranca'] + s['resiliencia'] + s['inovacao'] + s['execucao'];
    return Math.round(sum / 5);
  });

  radarDots = computed(() => {
    const s = this.scores();
    const cx = 150;
    const cy = 150;
    const maxR = 120;

    const angles = [
      -Math.PI / 2,
      -Math.PI / 2 + (2 * Math.PI / 5),
      -Math.PI / 2 + (4 * Math.PI / 5),
      -Math.PI / 2 + (6 * Math.PI / 5),
      -Math.PI / 2 + (8 * Math.PI / 5)
    ];

    const vals = [s['visao'], s['lideranca'], s['resiliencia'], s['inovacao'], s['execucao']];

    return angles.map((ang, i) => {
      const r = (vals[i] / 100) * maxR;
      return {
        x: cx + r * Math.cos(ang),
        y: cy + r * Math.sin(ang)
      };
    });
  });

  radarPolygonPoints = computed(() => {
    return this.radarDots().map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  });

  archetypeResult = computed(() => {
    const s = this.scores();
    if (s['lideranca'] >= 80 && s['visao'] >= 80) {
      return {
        title: "Founder & CTO de Startup de Telecom",
        desc: "Excelente equilíbrio entre visão estratégica de novos negócios e profundidade arquitetural em redes."
      };
    } else if (s['inovacao'] >= 80 && s['execucao'] >= 80) {
      return {
        title: "Empreendedor de Produto e Redes Ágeis",
        desc: "Foco total em prototipar rápido e colocar soluções funcionais diretamente na mão do cliente."
      };
    } else if (s['resiliencia'] >= 80 && s['lideranca'] >= 80) {
      return {
        title: "Intraempreendedor / Gestor de Redes Críticas",
        desc: "Alta estabilidade para liderar transformações em grandes infraestruturas corporativas existentes."
      };
    } else {
      return {
        title: "Arquiteto em Desenvolvimento Empreendedor",
        desc: "Base técnica sólida. Fortalecer a visão de mercado acelerará a criação de novos serviços de TIC."
      };
    }
  });
}
